import React, { useEffect, useState } from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  RadioGroup, RadioGroupItem,
} from '@/components/ui/radio-group';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlertTriangle, CheckCircle2, Loader2, FileStack, ExternalLink } from 'lucide-react';
import { contractsAdminAPI } from '@/lib/api';
import { formatDate, isKRRole } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

/**
 * Phase 24 — Duplicate Contract Review page.
 *
 * Shows every (npn, carrier_id, contract_type_id) group with more than one row
 * in `contract_requests`. KR picks the canonical row per group; non-canonical
 * siblings are archived (soft) and chained via supersedes_contract_id.
 */
export default function ContractDuplicateReviewPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // { group, canonical_request_id, reason }
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('unresolved');

  useEffect(() => {
    if (!isKRRole(user)) return;
    load();
  }, [user]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await contractsAdminAPI.duplicateGroups();
      setGroups(data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load duplicate groups');
    } finally { setLoading(false); }
  };

  if (!isKRRole(user)) {
    return <div className="p-8 text-center text-slate-500">KR admins only.</div>;
  }

  const visible = groups.filter((g) => filter === 'all' || !g.resolved);
  const unresolvedCount = groups.filter((g) => !g.resolved).length;

  const openResolveDialog = (group) => {
    const nonTerminal = group.rows.find((r) => !r.is_terminal);
    setDialog({ group, canonical_request_id: nonTerminal?.request_id || group.rows[0].request_id, reason: '' });
  };

  const doResolve = async () => {
    if (!dialog) return;
    if ((dialog.reason || '').trim().length < 5) {
      toast.error('Reason must be at least 5 characters');
      return;
    }
    setBusy(true);
    try {
      await contractsAdminAPI.resolveGroup({
        npn: dialog.group.key.npn,
        carrier_id: dialog.group.key.carrier_id,
        contract_type_id: dialog.group.key.contract_type_id || '',
        canonical_request_id: dialog.canonical_request_id,
        archived_reason: dialog.reason.trim(),
      });
      toast.success('Group resolved');
      setDialog(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to resolve');
    } finally { setBusy(false); }
  };

  return (
    <div className="page-content">
      <PageHeader
        title="Duplicate Contract Review"
        description="Pick one canonical contract per group. Non-canonical siblings will be archived and chained via supersedes."
        icon={<FileStack size={20} />}
      />

      <div className="flex items-center gap-2 mb-4">
        <Badge variant={filter === 'unresolved' ? 'default' : 'outline'}
               className="cursor-pointer"
               onClick={() => setFilter('unresolved')}
               data-testid="dup-filter-unresolved">
          Unresolved ({unresolvedCount})
        </Badge>
        <Badge variant={filter === 'all' ? 'default' : 'outline'}
               className="cursor-pointer"
               onClick={() => setFilter('all')}
               data-testid="dup-filter-all">
          All ({groups.length})
        </Badge>
      </div>

      {loading ? (
        <div className="py-16 text-center text-slate-400">
          <Loader2 className="mx-auto animate-spin" />
        </div>
      ) : visible.length === 0 ? (
        <div className="p-10 text-center text-slate-500">
          <CheckCircle2 className="mx-auto mb-2 text-emerald-500" size={28} />
          {filter === 'unresolved'
            ? 'No unresolved duplicate groups. Nice work.'
            : 'No duplicate groups.'}
        </div>
      ) : (
        <div className="space-y-4" data-testid="dup-group-list">
          {visible.map((g, idx) => (
            <Card key={idx} data-testid={`dup-group-${g.key.npn}-${g.key.carrier_id}`}>
              <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
                <CardTitle className="text-base">
                  <div className="flex items-center gap-2">
                    {g.resolved ? (
                      <CheckCircle2 size={16} className="text-emerald-500" />
                    ) : (
                      <AlertTriangle size={16} className="text-amber-500" />
                    )}
                    Agent NPN <span className="font-mono">{g.key.npn}</span>
                    <span className="text-slate-500 mx-1">·</span>
                    {g.rows[0]?.carrier_name}
                    <span className="text-slate-500 mx-1">·</span>
                    <span className="text-slate-600">{g.rows[0]?.contract_type_name}</span>
                  </div>
                </CardTitle>
                {g.resolved ? (
                  <Badge className="bg-emerald-100 text-emerald-800">Resolved</Badge>
                ) : (
                  <Button size="sm" onClick={() => openResolveDialog(g)}
                          data-testid={`dup-resolve-btn-${idx}`}>
                    Resolve
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase text-slate-500">
                      <th className="py-1">Status</th>
                      <th className="py-1">Writing #</th>
                      <th className="py-1">Created</th>
                      <th className="py-1">Chain</th>
                      <th className="py-1">Open</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.rows.map((r) => (
                      <tr key={r.request_id} className="border-t border-slate-100">
                        <td className="py-1.5">
                          <Badge className={
                            r.is_terminal ? 'bg-slate-200 text-slate-700' :
                            r.status === 'active' ? 'bg-emerald-100 text-emerald-800' :
                            r.status === 'approved' ? 'bg-sky-100 text-sky-800' :
                            'bg-amber-100 text-amber-800'
                          }>
                            {r.status || 'unknown'}
                          </Badge>
                        </td>
                        <td className="py-1.5 font-mono text-xs">{r.writing_number || '—'}</td>
                        <td className="py-1.5 text-xs text-slate-600">{formatDate(r.created_at)}</td>
                        <td className="py-1.5 text-xs text-slate-500">
                          {r.superseded_by_contract_id && '→ archived; superseded'}
                          {r.supersedes_contract_id && !r.superseded_by_contract_id && '↑ supersedes older'}
                          {r.archived_at && ` @ ${formatDate(r.archived_at)}`}
                        </td>
                        <td className="py-1.5">
                          <button
                            className="text-sky-600 hover:underline inline-flex items-center gap-1 text-xs"
                            onClick={() => navigate(`/requests/${r.request_id}`)}
                            data-testid={`dup-open-${r.request_id}`}
                          >
                            {r.request_id.slice(0, 8)}… <ExternalLink size={11} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Resolve dialog */}
      <AlertDialog open={!!dialog} onOpenChange={(v) => { if (!v && !busy) setDialog(null); }}>
        <AlertDialogContent data-testid="dup-resolve-dialog" className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Resolve duplicate group</AlertDialogTitle>
            <AlertDialogDescription>
              Pick the row that should remain as the canonical contract. All other non-terminal
              siblings will be archived (soft-delete) and chained via <code>supersedes_contract_id</code>.
              Nothing is deleted. Requires a mandatory reason.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {dialog && (
            <div className="space-y-4">
              <RadioGroup
                value={dialog.canonical_request_id}
                onValueChange={(v) => setDialog({ ...dialog, canonical_request_id: v })}
              >
                {dialog.group.rows.map((r) => (
                  <label key={r.request_id}
                         className="flex items-start gap-3 p-2 rounded-md border border-slate-200 hover:bg-slate-50 cursor-pointer">
                    <RadioGroupItem
                      value={r.request_id}
                      data-testid={`dup-pick-${r.request_id}`}
                      disabled={r.is_terminal}
                    />
                    <div className="flex-1 text-sm">
                      <div className="flex items-center gap-2">
                        <Badge className={
                          r.is_terminal ? 'bg-slate-200 text-slate-600' :
                          r.status === 'active' ? 'bg-emerald-100 text-emerald-800' :
                          r.status === 'approved' ? 'bg-sky-100 text-sky-800' :
                          'bg-amber-100 text-amber-800'
                        }>{r.status || 'unknown'}</Badge>
                        {r.writing_number && <span className="font-mono text-xs">WN {r.writing_number}</span>}
                        <span className="text-xs text-slate-500">created {formatDate(r.created_at)}</span>
                      </div>
                      <div className="text-xs text-slate-500 mt-1 font-mono">{r.request_id}</div>
                      {r.is_terminal && (
                        <div className="text-[11px] text-slate-400 italic">Terminal — cannot be canonical.</div>
                      )}
                    </div>
                  </label>
                ))}
              </RadioGroup>

              <div>
                <Label>Reason (minimum 5 characters)</Label>
                <Textarea
                  rows={2}
                  value={dialog.reason}
                  onChange={(e) => setDialog({ ...dialog, reason: e.target.value })}
                  placeholder="e.g. Duplicate submission — keeping the active writing number"
                  data-testid="dup-reason-input"
                />
              </div>
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={busy || !dialog || (dialog.reason || '').trim().length < 5}
              onClick={(e) => { e.preventDefault(); doResolve(); }}
              data-testid="dup-resolve-confirm"
            >
              {busy ? 'Resolving…' : 'Archive Duplicates'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
